import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { StudioCalendar, sessionLabel, studioClashes } from "./StudioCalendar";

const ACTIONS = {
  bookings: [["confirmed", "Confirm"], ["declined", "Decline"]],
  features: [["accepted", "Accept"], ["declined", "Decline"]],
  studio: [["approved", "Approve"], ["declined", "Decline"]],
};
const HEAD = {
  bookings: (r) => `${r.event_name} — ${r.date}`,
  features: (r) => `${r.artist_name} — "${r.track_title}"`,
  studio: (r) => `${r.name} — ${r.date} ${r.start_time} (${r.hours}h)`,
};
const HIDE = new Set(["id", "created_at", "status", "email_status", "email_error", "emailed_status"]);
const EMAIL_NOTE = { sending: "Sending reply email", sent: "Reply email sent", failed: "Reply email failed" };
const tone = (s) => (s === "pending" ? "text-amber-300 border-amber-300/40" : s === "declined" ? "text-red-300 border-red-300/40" : "text-lime border-[#00d436]/50");

export const RequestsManager = ({ kind }) => {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.adminRequests(kind).then(setRows).catch((e) => toast.error(errMsg(e))), [kind]);
  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (r, status) => {
    if (kind === "studio" && status === "approved") {
      const clash = studioClashes(r, rows);
      if (clash.length && !window.confirm(`This overlaps an approved session:\n${clash.map(sessionLabel).join("\n")}\n\nApprove anyway?`)) return;
    }
    try {
      await api.adminSetStatus(kind, r.id, status);
      toast.success(`Marked ${status}${status !== "pending" && status !== r.status ? " — reply email on its way" : ""}${kind === "bookings" && status === "confirmed" ? ". Date now shows on the concert calendar" : ""}`);
      load();
      setTimeout(load, 4000);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4" data-testid={`admin-${kind}-list`}>
      {kind === "studio" && <StudioCalendar key={rows.length ? "loaded" : "empty"} rows={rows} />}
      {rows.length === 0 && <p className="text-slate-500 text-sm">No requests yet.</p>}
      {rows.map((r) => (
        <div key={r.id} id={`studio-row-${r.id}`} className="card-surface rounded-xl p-5 scroll-mt-28" data-testid={`admin-${kind}-row-${r.id}`}>
          <div className="flex flex-wrap items-center gap-3 justify-between">
            <p className="font-semibold">{HEAD[kind](r)}</p>
            <span data-testid={`admin-${kind}-status-${r.id}`} className={`text-xs uppercase tracking-widest border rounded-full px-3 py-1 ${tone(r.status)}`}>{r.status}</span>
          </div>
          <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1 mt-3 text-sm">
            {Object.entries(r).filter(([k, v]) => !HIDE.has(k) && v !== "" && v !== null).map(([k, v]) => (
              <div key={k} className="min-w-0"><dt className="inline text-slate-500 capitalize">{k.replace(/_/g, " ")}: </dt><dd className="inline text-slate-300 break-words">{String(v)}</dd></div>
            ))}
          </dl>
          {kind === "studio" && r.status !== "declined" && studioClashes(r, rows).length > 0 && (
            <p data-testid={`admin-studio-clash-${r.id}`} className="text-xs mt-3 text-red-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Overlaps approved: {studioClashes(r, rows).map(sessionLabel).join(", ")}
            </p>
          )}
          {r.email_status && (
            <p data-testid={`admin-${kind}-email-${r.id}`} className={`text-xs mt-3 ${r.email_status === "failed" ? "text-red-300" : "text-slate-500"}`}>
              {EMAIL_NOTE[r.email_status]}{r.emailed_status ? ` (${r.emailed_status})` : ""}{r.email_status === "failed" && r.email_error ? ` — ${r.email_error}` : ""}
            </p>
          )}
          <div className="flex gap-2 mt-4">
            {ACTIONS[kind].map(([s, label]) => (
              <button key={s} disabled={r.status === s} onClick={() => setStatus(r, s)} data-testid={`admin-${kind}-${s}-${r.id}`}
                className={`${s === "declined" ? "btn-line" : "btn-lime"} rounded-full px-5 py-2 text-sm font-cond uppercase tracking-widest font-bold`}>
                {label}
              </button>
            ))}
            {r.status !== "pending" && (
              <button onClick={() => setStatus(r, "pending")} data-testid={`admin-${kind}-reset-${r.id}`} className="btn-line rounded-full px-5 py-2 text-sm font-cond uppercase tracking-widest">Reset</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
